import { ReactElement, useEffect } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { useAppSelector } from '../../lib/hooks/index'
import { APP_CONSTANTS } from '../../repository/constants/constants'
import { LocalStorageService } from '../../services/storage/local-storage.service'
import { TokenPrimeService } from '../../services/token/token.service'
import { AppRouteType, GuardFunc } from '../../types/router.types'

function GuardController(props: { route: AppRouteType }) {
  const navigate = useNavigate()
  // Call hooks at the top level of the component (React Rules of Hooks)
  const appState = useAppSelector((state) => state)
  const location = useLocation()

  useEffect(() => {
    async function checkTokenExpiration() {
      // Give a small delay to allow cookies to propagate after login
      // This prevents reading stale cookie data immediately after login
      await new Promise(resolve => setTimeout(resolve, 100))

      const tokenServicePrime = new TokenPrimeService()
      const tokenData = await tokenServicePrime.getTokenPairToCookie()
      const tokenExpires = tokenData?.description?.tokenExpires

      console.log('🔍 Guard - Token data:', { tokenData, tokenExpires })

      // Skip check if no token data or expiration time
      if (!tokenExpires) {
        console.log('🔍 Guard - No token expires, skipping check')
        return
      }

      const tokenExpiresNum = parseInt(tokenExpires.toString())
      const currentTime = Date.now()
      const timeUntilExpiration = tokenExpiresNum - currentTime

      console.log('🔍 Guard - Expiration check:', {
        tokenExpires: tokenExpiresNum,
        currentTime,
        timeUntilExpiration,
        expiresAt: new Date(tokenExpiresNum).toLocaleString(),
        currentTimeReadable: new Date(currentTime).toLocaleString(),
        isExpired: timeUntilExpiration < 0
      })

      // Only navigate to login if token is actually expired (negative time until expiration)
      // Also check that the expiration time is reasonable (not from the past like 2023)
      const isExpired = timeUntilExpiration < 0
      const isVeryOld = tokenExpiresNum < Date.now() - (365 * 24 * 60 * 60 * 1000) // More than 1 year in the past

      if (isExpired && !isVeryOld) {
        console.log('⏰ Token expired, clearing auth state')
        LocalStorageService.clearAuthenticatedState()
        tokenServicePrime.clearTokenPairToCookie()
        navigate('/login')
      } else if (isVeryOld) {
        console.log('⚠️ Token expiration is very old (stale cookie), ignoring to avoid false logout')
      } else {
        console.log('✅ Token still valid, no logout needed')
      }
    }

    checkTokenExpiration()
  }, [navigate])

  const { route } = props
  // eslint-disable-next-line
  let Component: any = <route.component />

  if (
    location.pathname !== APP_CONSTANTS.APP_PATHS.LOGIN &&
    location.pathname !== APP_CONSTANTS.APP_PATHS.REGISTER &&
    location.pathname !== APP_CONSTANTS.APP_PATHS.FORGOT_PASS &&
    location.pathname !== APP_CONSTANTS.APP_PATHS.VERIFY_PIN
  ) {
    localStorage.setItem(APP_CONSTANTS.STORAGE_KEY.PREVIOUS_ROUTE, location.pathname)
  }

  if (route.guards && route.guards.length > 0) {
    const caughtByGuard = 'guard-caught'
    try {
      route.guards.forEach((guard: GuardFunc) => {
        if (typeof guard === 'function') {
          // Use the hooks called at the top level instead of calling them here
          const g = guard({
            state: appState,
            location: location,
          })
          if (typeof g === 'string' && window.location.pathname !== g) {
            Component = <Navigate replace to={g} />
          } else if (typeof g === 'string' && window.location.pathname === g) {
            return Component
          } else if (typeof g === 'boolean' && !g) {
            Component = <Navigate replace to={APP_CONSTANTS.APP_PATHS.LOGIN} />
          }
        }
      })
      // eslint-disable-next-line
    } catch (e: any) {
      if (e?.message !== caughtByGuard) {
        throw e
      }
    }
  }

  // eslint-disable-next-line
  return Component as ReactElement<any, any>
}

export default GuardController
